import { CheckCircle2, Circle, Lock } from "lucide-react";

export const STAGE_SHORT = { 1: "Outreach", 2: "Discovery", 3: "Investor material", 4: "Report generated", 5: "Walkthrough", 6: "Advisor request" };
export const STAGE_COUNT = 6;
const MANUAL = {
  1: { flag: "outreach_done", date: "outreach_date" },
  3: { flag: "investor_material_done", date: "investor_material_date" },
  5: { flag: "walkthrough_done", date: "walkthrough_date" },
};
const AUTO_HINT = {
  2: "Completes when you submit the discovery form for this client",
  4: "Updates automatically when the client downloads their report, or when you generate it for them",
  6: "Updates automatically when the client requests an advisor session",
};

export const ClientPipeline = ({ r, onSave, onDiscovery }) => {
  const id = r.company_id;
  const p = r.pipeline || { stages: [], current_stage: 0, completed: 0, current_label: "Not started" };
  const patchStage = (n, patch) => {
    const stages = p.stages.map((s) => (s.n === n ? { ...s, ...patch } : s));
    const done = stages.filter((s) => s.done).map((s) => s.n);
    const current = done.length ? Math.max(...done) : 0;
    return { pipeline: { ...p, stages, completed: done.length, current_stage: current,
      current_label: current ? stages.find((s) => s.n === current).label : "Not started" } };
  };
  const toggle = (n, checked) => {
    const keys = MANUAL[n];
    const today = new Date().toISOString().slice(0, 10);
    const date = checked ? (p.stages.find((s) => s.n === n)?.date || today) : null;
    onSave(id, { [keys.flag]: checked, ...(checked ? { [keys.date]: date } : {}) }, patchStage(n, { done: checked, date }));
  };
  const setDate = (n, date) => {
    const keys = MANUAL[n];
    onSave(id, { [keys.date]: date, [keys.flag]: !!date }, patchStage(n, { date: date || null, done: !!date }));
  };

  return (
    <div className="mt-4 rounded-xl border border-[#0A8B85]/20 bg-[#0A8B85]/[0.04] p-3" data-testid={`rm-pipeline-${id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Client pipeline</p>
        <span className="rounded-full bg-[#0A2540] px-2.5 py-0.5 text-[11px] font-extrabold text-white" data-testid={`rm-pipeline-current-${id}`}>
          {p.current_stage ? `Stage ${p.current_stage} of ${STAGE_COUNT} · ${p.current_label}` : "Not started"}
        </span>
      </div>
      <div className="mt-2 flex gap-1" aria-hidden>
        {p.stages.map((s) => <span key={s.n} className={`h-1.5 flex-1 rounded-full ${s.done ? "bg-[#0A8B85]" : "bg-slate-200"}`} />)}
      </div>
      <ol className="mt-2.5 space-y-1.5">
        {p.stages.map((s) => {
          const manual = MANUAL[s.n];
          return (
            <li key={s.n} className={`flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${s.done ? "bg-white" : ""}`} data-testid={`rm-pipeline-stage-${s.n}-${id}`}>
              {manual ? (
                <input type="checkbox" checked={!!s.done} onChange={(e) => toggle(s.n, e.target.checked)}
                  className="h-3.5 w-3.5 accent-[#0A8B85]" data-testid={`rm-pipeline-check-${s.n}-${id}`} />
              ) : s.done ? <CheckCircle2 size={14} className="text-[#0A8B85]" /> : <Circle size={14} className="text-slate-300" />}
              <span className={`font-bold ${s.done ? "text-slate-800" : "text-slate-500"}`}>{s.n}.</span>
              <span className={`flex-1 ${s.done ? "font-semibold text-slate-800" : "text-slate-500"}`}>{s.label}</span>
              {s.n === 2 && onDiscovery && (
                <button type="button" onClick={onDiscovery}
                  className="rounded-md border border-[#0A8B85] bg-white px-2 py-0.5 text-[11px] font-bold text-[#0A8B85] hover:bg-[#0A8B85]/10"
                  data-testid={`rm-discovery-open-${id}`}>{s.done ? "View / update form" : "Open discovery form"}</button>
              )}
              {manual ? (
                <input type="date" value={s.date || ""} onChange={(e) => setDate(s.n, e.target.value)}
                  className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-700 outline-none focus:border-[#0A8B85]"
                  data-testid={`rm-pipeline-date-${s.n}-${id}`} />
              ) : s.done ? (
                <span className="text-[11px] font-semibold text-[#0A8B85]" data-testid={`rm-pipeline-date-${s.n}-${id}`}>{s.date}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-400" title={AUTO_HINT[s.n]}><Lock size={10} /> auto</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
